import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdCheck, MdClose, MdEdit, MdSave, MdGridView, MdDehaze, MdPrint, MdDownload, MdDescription } from 'react-icons/md';

import Card from 'components/card';
import ConfirmModal from 'components/modals/ConfirmModal';
import {
    getPreparationById,
    validatePreparation,
    cancelPreparation,
    updatePreparationStatus,
    updatePreparationLine,
    updatePreparationNotes,
    getProductInventory,
} from 'interfaces/orderPreparation';
import { IOrderPreparation, IPreparationLine, IPreparationInventoryLine } from 'interfaces/orderPreparationTypes';
import { getOrderDeliveryNote, getOrderDeliveryNoteWithoutPrice } from 'interfaces/order';
import { dateToFrench } from 'utils/date';

/**
 * Returns the quantity that quantityPrepared should reach, expressed in the same unit
 * as quantityPrepared (i.e. base units when billByBaseUnit, packaging units otherwise).
 */
const getPreparedTarget = (line: IPreparationLine): number => {
    const weight = line.packaging?.weight || 1;
    const hasPkg = !!(line.packaging && line.packaging.unity);
    const billByBase = !!(hasPkg && line.packaging?.billByBaseUnit);
    return hasPkg && billByBase ? line.quantityOrdered * weight : line.quantityOrdered;
};

const statusLabels = {
    draft: 'Brouillon',
    in_progress: 'En cours',
    completed: 'Terminée',
    cancelled: 'Annulée'
};

const statusColors = {
    draft: 'bg-gray-500',
    in_progress: 'bg-[#0260CB]',
    completed: 'bg-green-500',
    cancelled: 'bg-red-500'
};

const ViewPreparation = () => {
    const { id } = useParams<{ id: string }>();
    const nav = useNavigate();
    const [preparation, setPreparation] = useState<IOrderPreparation | null>(null);
    const [loading, setLoading] = useState(true);
    const [editingNotes, setEditingNotes] = useState(false);
    const [notes, setNotes] = useState('');
    const [editingInventory, setEditingInventory] = useState<{
        productId: string;
        inventoryLines: IPreparationInventoryLine[];
    } | null>(null);
    const [availableInventory, setAvailableInventory] = useState<any[]>([]);
    const [productInventories, setProductInventories] = useState<{[productId: string]: any[]}>({});
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
    const [showValidateModal, setShowValidateModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [validateModalContent, setValidateModalContent] = useState<{
        title: string;
        description: React.ReactNode;
        isWarning: boolean;
    }>({
        title: '',
        description: '',
        isWarning: false
    });
    const [validateModalExtraAction, setValidateModalExtraAction] = useState<{
        label: string;
        onClick: () => void;
        color?: string;
    } | undefined>(undefined);
    const [productsWithComment, setProductsWithComment] = useState<{[productId: string]: boolean}>({});
    const [showBLModal, setShowBLModal] = useState(false);
    const [linesView, setLinesView] = useState<'cards' | 'table'>(
        () => (localStorage.getItem('preparation-lines-view') as 'cards' | 'table') || 'cards'
    );

    const handleSetLinesView = (v: 'cards' | 'table') => {
        setLinesView(v);
        localStorage.setItem('preparation-lines-view', v);
    };

    // Table view – quick quantity editing
    const [tableQtyDraft, setTableQtyDraft] = useState<Record<string, string>>({});
    const [tableQtySaving, setTableQtySaving] = useState<Record<string, boolean>>({});

    const handleTableQtySave = async (line: IPreparationLine, explicitValue?: string) => {
        if (!id) return;
        const draft = explicitValue !== undefined ? explicitValue : tableQtyDraft[line.productId];
        if (draft === undefined) return;
        const enteredQty = parseFloat(draft);
        if (isNaN(enteredQty)) {
            setTableQtyDraft((prev) => { const n = { ...prev }; delete n[line.productId]; return n; });
            return;
        }
        // If billByBaseUnit, the user entered in base units → store as-is (base unit quantity)
        const hasPkg = !!(line.packaging && line.packaging.unity);
        const billByBase = !!(hasPkg && line.packaging?.billByBaseUnit);
        const weight = line.packaging?.weight || 1;
        const newQty = billByBase ? enteredQty : enteredQty;
        if (newQty === line.quantityPrepared) {
            setTableQtyDraft((prev) => { const n = { ...prev }; delete n[line.productId]; return n; });
            return;
        }
        setTableQtySaving((prev) => ({ ...prev, [line.productId]: true }));
        // Keep lot detail if there is exactly one existing inventory line, otherwise use a MANUAL entry
        const newInventoryLines: IPreparationInventoryLine[] = line.inventoryLines.length === 1
            ? [{ ...line.inventoryLines[0], quantityPicked: newQty }]
            : [{ inventoryId: 'MANUAL', area: 'N/A', quantityPicked: newQty, quantityAvailable: 9999 }];
        const res = await updatePreparationLine(id, line.productId, { inventoryLines: newInventoryLines });
        if (res) {
            setPreparation(res);
            setTableQtyDraft((prev) => { const n = { ...prev }; delete n[line.productId]; return n; });
        }
        setTableQtySaving((prev) => ({ ...prev, [line.productId]: false }));
    };

    /**
     * Sort preparation lines by category and subcategory position
     * Products without category/subcategory are placed at the end
     */
    const sortPreparationLines = (lines: IPreparationLine[]): IPreparationLine[] => {
        return [...lines].sort((a, b) => {
            // Products with subcategory
            const aHasSubCat = !!a.subCategory;
            const bHasSubCat = !!b.subCategory;
            
            // Products with category (but no subcategory)
            const aHasCat = !!a.category && !aHasSubCat;
            const bHasCat = !!b.category && !bHasSubCat;
            
            // Products without any category
            const aHasNone = !a.category && !a.subCategory;
            const bHasNone = !b.category && !b.subCategory;
            
            // Products without category go to the end
            if (aHasNone && !bHasNone) return 1;
            if (!aHasNone && bHasNone) return -1;
            if (aHasNone && bHasNone) return 0;
            
            // Both have subcategory - sort by category position, then subcategory position
            if (aHasSubCat && bHasSubCat) {
                // First compare parent category position
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                if (aCatPos !== bCatPos) {
                    return aCatPos - bCatPos;
                }
                
                // Same parent category, compare subcategory position
                const aSubCatPos = a.subCategory?.position ?? 999999;
                const bSubCatPos = b.subCategory?.position ?? 999999;
                
                return aSubCatPos - bSubCatPos;
            }
            
            // One has subcategory, one has only category
            if (aHasSubCat && bHasCat) {
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                if (aCatPos !== bCatPos) {
                    return aCatPos - bCatPos;
                }
                // Same category - product with subcategory comes first
                return -1;
            }
            
            if (aHasCat && bHasSubCat) {
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                if (aCatPos !== bCatPos) {
                    return aCatPos - bCatPos;
                }
                // Same category - product with subcategory comes first
                return 1;
            }
            
            // Both have only category - sort by category position
            if (aHasCat && bHasCat) {
                const aCatPos = a.category?.position ?? 999999;
                const bCatPos = b.category?.position ?? 999999;
                
                return aCatPos - bCatPos;
            }
            
            return 0;
        });
    };

    const fetchPreparation = async () => {
        if (!id) return;
        setLoading(true);
        const res = await getPreparationById(id);
        if (res) {
            setPreparation(res);
            setNotes(res.notes || '');
            
            // Load inventory only for products with insufficient quantity
            const insufficientProducts = res.preparationLines.filter(
                (line: IPreparationLine) => line.quantityPrepared < getPreparedTarget(line)
            );
            
            // Initialize comment checkboxes to false by default for all insufficient products
            const initialComments: {[productId: string]: boolean} = {};
            insufficientProducts.forEach((line: IPreparationLine) => {
                initialComments[line.productId] = false;
            });
            setProductsWithComment(initialComments);
            
            if (insufficientProducts.length > 0) {
                const inventoryPromises = insufficientProducts.map(async (line: IPreparationLine) => {
                    const inventory = await getProductInventory(line.productId);
                    return { productId: line.productId, inventory: inventory || [] };
                });
                
                const inventories = await Promise.all(inventoryPromises);
                const inventoryMap: {[key: string]: any[]} = {};
                inventories.forEach(item => {
                    inventoryMap[item.productId] = item.inventory;
                });
                setProductInventories(inventoryMap);
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchPreparation();
    }, [id]);

    // Auto-save inventory lines with debounce
    useEffect(() => {
        if (!editingInventory || !id) return;
        
        setSaveStatus('saving');
        const delayDebounceFn = setTimeout(async () => {
            // Validate that all required fields are filled
            // For MANUAL entries, we allow saving even if area/lot are empty (user might be filling them)
            const hasEmptyFields = editingInventory.inventoryLines.some(
                inv => !inv.inventoryId || inv.quantityPicked <= 0
            );
            
            if (hasEmptyFields) {
                setSaveStatus('idle');
                return;
            }
            
            // Filter out any lines with empty inventoryId and prepare data
            const validLines = editingInventory.inventoryLines
                .filter(inv => inv.inventoryId && inv.inventoryId.trim() !== '')
                .map(inv => ({
                    inventoryId: inv.inventoryId,
                    area: inv.area || 'N/A', // Default to 'N/A' if not specified
                    lotNumber: inv.lotNumber || undefined,
                    dlc: inv.dlc || undefined,
                    quantityPicked: inv.quantityPicked,
                    quantityAvailable: inv.quantityAvailable
                }));
            
            const res = await updatePreparationLine(id, editingInventory.productId, {
                inventoryLines: validLines
            });
            
            if (res) {
                setSaveStatus('saved');
                // Refresh preparation in background without closing edit mode
                const updatedPrep = await getPreparationById(id);
                if (updatedPrep) {
                    setPreparation(updatedPrep);
                }
            } else {
                setSaveStatus('error');
            }
        }, 1500);
        
        return () => clearTimeout(delayDebounceFn);
    }, [editingInventory, id]);

    const handleValidate = async () => {
        if (!preparation || !id) return;

        // Flush any pending table qty drafts before checking completeness
        const pendingEntries = Object.entries(tableQtyDraft);
        if (pendingEntries.length > 0) {
            await Promise.all(
                pendingEntries.map(([productId, draftVal]) => {
                    const line = preparation.preparationLines.find((l: IPreparationLine) => l.productId === productId);
                    if (!line) return Promise.resolve();
                    return handleTableQtySave(line, draftVal);
                })
            );
        }

        // Re-fetch preparation after potential flush to get accurate quantities
        const currentPrep = await getPreparationById(id);
        const linesToCheck = currentPrep ? currentPrep.preparationLines : preparation.preparationLines;
        if (currentPrep) setPreparation(currentPrep);

        // Check for incomplete products
        const incompleteProducts = linesToCheck.filter(
            (line: IPreparationLine) => line.quantityPrepared < getPreparedTarget(line)
        );
        
        if (incompleteProducts.length > 0) {
            // Detect lines that are untouched (quantityPrepared === 0) — those show pre-selections
            const untouchedLines = incompleteProducts.filter((line: IPreparationLine) => line.quantityPrepared === 0);
            const hasUntouched = untouchedLines.length > 0;

            const productList = (
                <div className="mt-3">
                    <p className="mb-3 font-semibold">Les produits suivants n'ont pas été complètement préparés :</p>
                    <ul className="list-disc list-inside space-y-2 mb-4 text-sm">
                        {incompleteProducts.map((line: IPreparationLine, idx: number) => {
                            const missing = getPreparedTarget(line) - line.quantityPrepared;
                            const isUntouched = line.quantityPrepared === 0;
                            return (
                                <li key={idx} className={isUntouched ? 'text-blue-600' : 'text-orange-600'}>
                                    <strong>{line.productName}</strong> - {isUntouched
                                        ? <span>présélection : <strong>{line.quantityOrdered} {line.unity}</strong></span>
                                        : <span>manque <strong>{missing} {line.unity}</strong></span>}
                                    {productsWithComment[line.productId] && <span className="ml-2 text-xs">(commentaire ajouté)</span>}
                                </li>
                            );
                        })}
                    </ul>
                    {hasUntouched ? (
                        <p className="text-sm text-blue-700">
                            Les lignes en bleu n'ont pas été saisies. Vous pouvez <strong>appliquer les présélections</strong> (quantités commandées) pour ces lignes, ou forcer la validation avec 0.
                        </p>
                    ) : (
                        <p className="text-sm">
                            En validant, vous <strong>modifierez la commande client</strong> avec les quantités réellement préparées.
                        </p>
                    )}
                </div>
            );

            // If there are any untouched lines, offer a preselect button
            if (hasUntouched) {
                setValidateModalExtraAction({
                    label: '✓ Valider avec les présélections',
                    color: 'bg-[#0260CB]',
                    onClick: async () => {
                        if (!id) return;
                        // Run sequentially to avoid Mongoose VersionError (concurrent writes on same document)
                        for (const line of untouchedLines) {
                            const weight = line.packaging?.weight || 1;
                            const hasPkg = !!(line.packaging && line.packaging.unity);
                            const billByBase = !!(hasPkg && line.packaging?.billByBaseUnit);
                            const qtyToSave = billByBase ? line.quantityOrdered * weight : line.quantityOrdered;
                            const newInventoryLines: IPreparationInventoryLine[] =
                                line.inventoryLines.length === 1
                                    ? [{ ...line.inventoryLines[0], quantityPicked: qtyToSave }]
                                    : [{ inventoryId: 'MANUAL', area: 'N/A', quantityPicked: qtyToSave, quantityAvailable: 9999 }];
                            await updatePreparationLine(id, line.productId, { inventoryLines: newInventoryLines });
                        }
                        await validatePreparation(id, productsWithComment);
                        fetchPreparation();
                    }
                });
            } else {
                setValidateModalExtraAction(undefined);
            }

            setValidateModalContent({
                title: 'ATTENTION !',
                description: productList,
                isWarning: true
            });
        } else {
            setValidateModalExtraAction(undefined);
            setValidateModalContent({
                title: 'Valider la préparation',
                description: 'Êtes-vous sûr de vouloir valider cette préparation ? Cette action est irréversible.',
                isWarning: false
            });
        }
        
        setShowValidateModal(true);
    };

    const confirmValidate = async () => {
        if (!id) return;
        const res = await validatePreparation(id, productsWithComment);
        if (res) {
            fetchPreparation();
        }
    };

    const handleCancel = () => {
        setShowCancelModal(true);
    };

    const confirmCancel = async () => {
        if (!id) return;
        const res = await cancelPreparation(id);
        if (res) {
            fetchPreparation();
        }
    };

    const handleChangeStatus = async (status: string) => {
        if (!id) return;
        const res = await updatePreparationStatus(id, status);
        if (res) {
            fetchPreparation();
        }
    };

    const handleSaveNotes = async () => {
        if (!id) return;
        const res = await updatePreparationNotes(id, notes);
        if (res) {
            setEditingNotes(false);
            fetchPreparation();
        }
    };

    const handleEditInventoryLines = (productId: string, inventoryLines: IPreparationInventoryLine[]) => {
        setEditingInventory({
            productId,
            inventoryLines: JSON.parse(JSON.stringify(inventoryLines)) // Deep copy
        });
        
        // Load available inventory for this product
        loadProductInventory(productId);
    };

    const loadProductInventory = async (productId: string) => {
        const inventory = await getProductInventory(productId);
        if (inventory) {
            setAvailableInventory(inventory);
        }
    };

    const handleCancelEditInventory = () => {
        setEditingInventory(null);
        setSaveStatus('idle');
    };

    const handleUpdateInventoryLine = (invIndex: number, field: keyof IPreparationInventoryLine, value: any) => {
        if (!editingInventory) return;
        
        const updatedLines = [...editingInventory.inventoryLines];
        updatedLines[invIndex] = {
            ...updatedLines[invIndex],
            [field]: value
        };
        
        setEditingInventory({
            ...editingInventory,
            inventoryLines: updatedLines
        });
    };

    const handleAddInventoryLine = () => {
        if (!editingInventory || !preparation) return;
        
        // Find the current product line to get quantityOrdered
        const currentLine = preparation.preparationLines.find(
            line => line.productId === editingInventory.productId
        );
        
        if (!currentLine) return;
        
        // Calculate how much is still needed
        const currentTotal = editingInventory.inventoryLines.reduce(
            (sum, inv) => sum + (inv.quantityPicked || 0), 
            0
        );
        const quantityNeeded = currentLine.quantityOrdered - currentTotal;
        
        // Get already selected inventory IDs
        const selectedIds = editingInventory.inventoryLines
            .map(line => line.inventoryId)
            .filter(id => id && id.trim() !== '');
        
        // Find the first available inventory not already selected (FIFO - oldest first)
        const productInventory = productInventories[editingInventory.productId] || [];
        const nextAvailableInventory = productInventory.find(
            stock => !selectedIds.includes(stock.id)
        );
        
        let newLine: IPreparationInventoryLine;
        
        if (nextAvailableInventory && quantityNeeded > 0) {
            // Pre-fill with the oldest available lot
            const quantityToPick = Math.min(quantityNeeded, nextAvailableInventory.quantity);
            
            newLine = {
                inventoryId: nextAvailableInventory.id,
                area: nextAvailableInventory.area,
                lotNumber: nextAvailableInventory.lotNumber || '',
                dlc: nextAvailableInventory.dlc || '',
                quantityPicked: quantityToPick,
                quantityAvailable: nextAvailableInventory.quantity
            };
        } else {
            // No more available inventory or nothing needed - initialize with manual entry
            newLine = {
                inventoryId: 'MANUAL',
                area: '',
                lotNumber: '',
                dlc: '',
                quantityPicked: quantityNeeded > 0 ? quantityNeeded : 0,
                quantityAvailable: 9999
            };
        }
        
        setEditingInventory({
            ...editingInventory,
            inventoryLines: [...editingInventory.inventoryLines, newLine]
        });
    };

    const handleRemoveInventoryLine = (invIndex: number) => {
        if (!editingInventory) return;
        
        const updatedLines = editingInventory.inventoryLines.filter((_, idx) => idx !== invIndex);
        
        setEditingInventory({
            ...editingInventory,
            inventoryLines: updatedLines
        });
    };

    const isEditable = preparation?.status === 'draft' || preparation?.status === 'in_progress';

    if (loading) {
        return (
            <div className="mt-3 flex h-[600px] items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-toola-500 border-r-transparent"></div>
                    <p className="text-gray-600">Chargement de la préparation...</p>
                </div>
            </div>
        );
    }

    if (!preparation) {
        return (
            <div className="mt-3 flex h-[600px] items-center justify-center">
                <Card extra="p-8">
                    <p className="text-xl font-semibold text-gray-600">Préparation introuvable</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="mt-3 h-full">
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => nav('/preparations')}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-lightPrimary text-[#0260CB] transition duration-200 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10"
                    >
                        <MdArrowBack className="h-5 w-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                            Préparation - {preparation.orderNumber}
                            {preparation.customerName && (
                                <span className="ml-2 text-xl font-semibold text-[#0260CB]">
                                    - {preparation.customerName}
                                </span>
                            )}
                        </h1>
                        <p className="text-sm text-gray-600">
                            Créée le {dateToFrench(preparation.createdAt)}
                        </p>
                        {preparation.deliveryNote && (
                            <div className="mt-1 flex items-start gap-2">
                                <p className="text-sm font-medium text-gray-700">Note de livraison :</p>
                                <p className="text-sm text-gray-600 italic">{preparation.deliveryNote}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap justify-end">
                    {preparation.orderId && (
                        <button
                            onClick={() => setShowBLModal(true)}
                            className="linear flex items-center gap-2 rounded-xl border border-[#01499b] px-3 py-2 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 whitespace-nowrap"
                        >
                            <MdDescription className="h-4 w-4" />
                            Bon de livraison
                        </button>
                    )}
                    {isEditable && (
                        <>
                            {preparation.status === 'draft' && (
                                <button
                                    onClick={() => handleChangeStatus('in_progress')}
                                    className="linear flex items-center gap-2 rounded-xl bg-[#0260CB] px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-[#01499b] active:bg-[#024086]"
                                >
                                    Démarrer la préparation
                                </button>
                            )}
                            <button
                                onClick={handleValidate}
                                className="linear flex items-center gap-2 rounded-xl bg-green-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-green-600 active:bg-green-700"
                            >
                                <MdCheck className="h-5 w-5" />
                                Valider
                            </button>
                            <button
                                onClick={handleCancel}
                                className="linear flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-red-600 active:bg-red-700"
                            >
                                <MdClose className="h-5 w-5" />
                                Annuler
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Status and Info Card */}
            <Card extra="mb-5 p-6 !border !border-[#7090B0]/[0.25]">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div>
                        <p className="text-sm font-medium text-gray-600">Statut</p>
                        <span className={`mt-1 inline-block rounded-xl px-3 py-1 text-sm font-medium text-white ${statusColors[preparation.status as keyof typeof statusColors]}`}>
                            {statusLabels[preparation.status as keyof typeof statusLabels]}
                        </span>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-600">Produits</p>
                        <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
                            {preparation.preparationLines.length}
                        </p>
                    </div>

                    {preparation.preparedBy && (
                        <div>
                            <p className="text-sm font-medium text-gray-600">Préparateur</p>
                            <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
                                {preparation.preparedBy.fullName}
                            </p>
                        </div>
                    )}

                    {preparation.validatedBy && (
                        <div>
                            <p className="text-sm font-medium text-gray-600">Validé par</p>
                            <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">
                                {preparation.validatedBy.fullName}
                            </p>
                        </div>
                    )}
                </div>

                {/* Customer notes */}
                {(preparation as any).customerNotes && (
                    <div className="mt-6">
                        <p className="mb-1 text-sm font-medium text-gray-600">Notes client</p>
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                            <p className="text-sm text-blue-800 whitespace-pre-line">{(preparation as any).customerNotes}</p>
                        </div>
                    </div>
                )}

                {/* Customer delivery notes */}
                {(preparation as any).customerDeliveryNotes && (
                    <div className="mt-6">
                        <p className="mb-1 text-sm font-medium text-gray-600">Notes utiles client</p>
                        <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
                            <p className="text-sm text-orange-800 whitespace-pre-line">{(preparation as any).customerDeliveryNotes}</p>
                        </div>
                    </div>
                )}

                {/* Comment */}
                <div className="mt-6">
                    <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-600">Notes générales</p>
                        {isEditable && !editingNotes && (
                            <button
                                onClick={() => setEditingNotes(true)}
                                className="text-[#0260CB] hover:text-[#01499b]"
                            >
                                <MdEdit className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    {editingNotes ? (
                        <div className="flex gap-2">
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="flex-1 rounded-xl border border-gray-300 p-2 text-sm outline-none focus:border-[#0260CB]"
                                rows={3}
                                placeholder="Ajoutez des notes..."
                            />
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={handleSaveNotes}
                                    className="rounded-xl bg-[#0260CB] p-2 text-white hover:bg-[#01499b]"
                                >
                                    <MdSave className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingNotes(false);
                                        setNotes(preparation.notes || '');
                                    }}
                                    className="rounded-xl bg-gray-300 p-2 text-gray-700 hover:bg-gray-400"
                                >
                                    <MdClose className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="text-navy-700 dark:text-white">
                            {preparation.notes || 'Aucune note'}
                        </p>
                    )}
                </div>
            </Card>

            {/* Preparation Lines */}
            <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                    Lignes de préparation
                </h2>
                <div className="flex items-center gap-2">
                    <MdGridView
                        size={22}
                        className={`cursor-pointer transition-colors ${
                            linesView === 'cards' ? 'text-[#0260CB]' : 'text-gray-400 hover:text-gray-600'
                        }`}
                        onClick={() => handleSetLinesView('cards')}
                        title="Vue cartes"
                    />
                    <MdDehaze
                        size={22}
                        className={`cursor-pointer transition-colors ${
                            linesView === 'table' ? 'text-[#0260CB]' : 'text-gray-400 hover:text-gray-600'
                        }`}
                        onClick={() => handleSetLinesView('table')}
                        title="Vue tableau"
                    />
                </div>
            </div>

            {/* ── Table view ── */}
            {linesView === 'table' && (() => {
                const sortedLines = sortPreparationLines(preparation.preparationLines);
                return (
                    <Card extra="!border !border-[#7090B0]/[0.25] overflow-x-auto">
                        <table className="w-full">
                            <thead className="hidden sm:contents">
                                <tr className="!border-px !border-gray-400">
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">Produit</p>
                                    </th>
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">Conditionnement</p>
                                    </th>
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">Qté commandée</p>
                                    </th>
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">Qté préparée</p>
                                    </th>
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">Zone</p>
                                    </th>
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">Lot</p>
                                    </th>
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">DLC</p>
                                    </th>
                                    <th className="mt-[20px] border-b border-gray-200 pb-2 px-[10px] pt-4 text-start dark:border-white/10">
                                        <p className="text-base font-bold text-gray-600 dark:text-white">Statut</p>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedLines.map((line: IPreparationLine, index: number) => {
                                    const isOk = line.quantityPrepared >= getPreparedTarget(line);
                                    // One row per inventory line, or a single row if no inventory lines
                                    const rows = line.inventoryLines.length > 0 ? line.inventoryLines : [null];
                                    return rows.map((inv, invIdx) => (
                                        <tr key={`${index}-${invIdx}`} className="hover:bg-gray-50 dark:hover:bg-navy-700">
                                            {invIdx === 0 && (
                                                <>
                                                    <td
                                                        rowSpan={rows.length}
                                                        className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px] align-top"
                                                    >
                                                        <p className="text-base font-semibold text-navy-700 dark:text-white">
                                                            {line.productName}
                                                        </p>
                                                        {line.productRef && (
                                                            <p className="mt-[2px] text-sm text-gray-500">{line.productRef}</p>
                                                        )}
                                                    </td>
                                                    <td
                                                        rowSpan={rows.length}
                                                        className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px] align-top"
                                                    >
                                                        {line.packaging && (line.packaging.unity || line.packaging.ref) ? (
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {line.packaging.unity || line.packaging.ref}
                                                                {line.packaging.weight && (
                                                                    <span className="text-gray-400"> ({line.packaging.weight} {line.unity})</span>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-500">{line.unity}</span>
                                                        )}
                                                    </td>
                                                    <td
                                                        rowSpan={rows.length}
                                                        className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px] align-top"
                                                    >
                                                        <p className="text-base font-semibold text-navy-700 dark:text-white">
                                                            {line.packaging && line.packaging.unity
                                                                ? (() => {
                                                                    const w = line.packaging.weight || 1;
                                                                    const billByBase = !!(line.packaging?.billByBaseUnit);
                                                                    return billByBase ? (
                                                                        <>{parseFloat((line.quantityOrdered * w).toFixed(2))} {line.unity}<span className="ml-1 text-sm text-gray-400">(= {line.quantityOrdered} {line.packaging.unity})</span></>
                                                                    ) : (
                                                                        <>{line.quantityOrdered} {line.packaging.unity}<span className="ml-1 text-sm text-gray-400">({line.quantityOrdered * w} {line.unity})</span></>
                                                                    );
                                                                })()
                                                                : <>{line.quantityOrdered} {line.unity}</>}
                                                        </p>
                                                    </td>
                                                    <td
                                                        rowSpan={rows.length}
                                                        className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px] align-top"
                                                    >
                                                        {(() => {
                                                            const weight = line.packaging?.weight || 1;
                                                            const hasPkg = !!(line.packaging && line.packaging.unity);
                                                            const billByBase = !!(hasPkg && line.packaging?.billByBaseUnit);
                                                            const pkgUnit = line.packaging?.unity || line.unity;
                                                            // If billByBase: input & stored value are in base units; otherwise in packaging units
                                                            const inputUnit = billByBase ? line.unity : pkgUnit;
                                                            const storedQty = line.quantityPrepared; // in base units if billByBase, else packaging units
                                                            const defaultQty = storedQty > 0 ? storedQty : (billByBase ? line.quantityOrdered * weight : line.quantityOrdered);
                                                            const draftVal = tableQtyDraft[line.productId];
                                                            const currentQty = draftVal !== undefined ? draftVal : String(defaultQty);
                                                            const saving = tableQtySaving[line.productId];
                                                            // Packaging equivalent: base qty / weight
                                                            const pkgEquiv = billByBase
                                                                ? (parseFloat(currentQty) || 0) / weight
                                                                : (parseFloat(currentQty) || 0) * weight;
                                                            return (
                                                                <div className="flex flex-col gap-1.5">
                                                                    {isEditable ? (
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <input
                                                                                id={`table-qty-${index}`}
                                                                                type="number"
                                                                                min={0}
                                                                                step={0.1}
                                                                                value={currentQty}
                                                                                onChange={(e) => setTableQtyDraft((prev) => ({ ...prev, [line.productId]: e.target.value }))}
                                                                                onFocus={(e) => e.target.select()}
                                                                                onBlur={(e) => handleTableQtySave(line, e.target.value)}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        (e.target as HTMLInputElement).blur();
                                                                                    } else if (e.key === 'ArrowDown') {
                                                                                        e.preventDefault();
                                                                                        const next = document.getElementById(`table-qty-${index + 1}`) as HTMLInputElement | null;
                                                                                        if (next) { next.focus(); setTimeout(() => next.select(), 0); }
                                                                                    } else if (e.key === 'ArrowUp') {
                                                                                        e.preventDefault();
                                                                                        const prev = document.getElementById(`table-qty-${index - 1}`) as HTMLInputElement | null;
                                                                                        if (prev) { prev.focus(); setTimeout(() => prev.select(), 0); }
                                                                                    }
                                                                                }}
                                                                                className={`w-[80px] h-[32px] py-1 px-2 border text-sm rounded-xl outline-none text-gray-700 focus:ring focus:border-blue-300 transition-colors ${
                                                                                    saving ? 'border-orange-300 bg-orange-50' : 'border-gray-300 bg-white'
                                                                                }`}
                                                                            />
                                                                            <span className="text-sm font-bold text-navy-700 dark:text-white whitespace-nowrap">
                                                                                {inputUnit}
                                                                            </span>
                                                                            {saving && (
                                                                                <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-orange-400" />
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        // Non-editable: if billByBase show base unit total prominently, pkg qty in parentheses
                                                                        billByBase ? (
                                                                            <p className={`text-base font-bold ${isOk ? 'text-green-600' : 'text-orange-600'}`}>
                                                                                {parseFloat((storedQty * weight).toFixed(2))} {line.unity}
                                                                                <span className="ml-1 text-sm font-normal text-gray-400">({parseFloat(storedQty.toFixed(2))} {pkgUnit})</span>
                                                                            </p>
                                                                        ) : (
                                                                            <p className={`text-base font-bold ${isOk ? 'text-green-600' : 'text-orange-600'}`}>
                                                                                {parseFloat(storedQty.toFixed(2))} {inputUnit}
                                                                            </p>
                                                                        )
                                                                    )}
                                                                    {/* Conversion under the input : only if packaging + billByBase */}
                                                                    {billByBase && (
                                                                        <p className="text-sm text-gray-400 whitespace-nowrap">
                                                                            = {isEditable ? pkgEquiv.toFixed(2) : parseFloat((storedQty / weight).toFixed(2))} {pkgUnit}
                                                                        </p>
                                                                    )}
                                                                    {/* Conversion packaging without billByBase */}
                                                                    {hasPkg && !billByBase && (
                                                                        <p className="text-sm text-gray-400 whitespace-nowrap">
                                                                            = {parseFloat(((parseFloat(currentQty) || storedQty) * weight).toFixed(2))} {line.unity}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                </>
                                            )}
                                            {/* Per-inventory-line cells */}
                                            <td className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px]">
                                                <p className="text-base text-navy-700 dark:text-white">
                                                    {inv ? inv.area || '—' : '—'}
                                                </p>
                                            </td>
                                            <td className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px]">
                                                <p className="text-base font-medium text-navy-700 dark:text-white">
                                                    {inv ? (inv.lotNumber || 'N/A') : '—'}
                                                </p>
                                            </td>
                                            <td className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px]">
                                                <p className="text-base text-navy-700 dark:text-white">
                                                    {inv && inv.dlc ? dateToFrench(inv.dlc) : 'N/A'}
                                                </p>
                                            </td>
                                            {invIdx === 0 && (
                                                <td
                                                    rowSpan={rows.length}
                                                    className="hidden sm:table-cell border-b text-sm dark:border-white/10 px-[10px] py-[14px] align-top"
                                                >
                                                    <span className={`inline-block rounded-xl px-3 py-1 text-xs font-semibold text-white ${
                                                        isOk ? 'bg-green-500' : 'bg-orange-500'
                                                    }`}>
                                                        {isOk ? '✓ Complet' : `⚠ ${getPreparedTarget(line) - line.quantityPrepared} manquant${(getPreparedTarget(line) - line.quantityPrepared) > 1 ? 's' : ''}`}
                                                    </span>
                                                </td>
                                            )}
                                        </tr>
                                    ));
                                })}
                            </tbody>
                        </table>
                    </Card>
                );
            })()}

            {/* ── card view ── */}
            <div className={linesView === 'cards' ? 'space-y-4' : 'hidden'}>
                {(() => {
                    const sortedLines = sortPreparationLines(preparation.preparationLines);
                    let currentCategory: string | null = null;
                    let currentSubCategory: string | null = null;
                    
                    return sortedLines.map((line: IPreparationLine, index: number) => {
                        const showCategoryHeader = line.category && line.category.id !== currentCategory;
                        const showSubCategoryHeader = line.subCategory && line.subCategory.id !== currentSubCategory;
                        
                        if (showCategoryHeader) {
                            currentCategory = line.category!.id;
                            currentSubCategory = null; // Reset subcategory when category changes
                        }
                        
                        if (showSubCategoryHeader) {
                            currentSubCategory = line.subCategory!.id;
                        }
                        
                        return (
                            <div key={index}>
                                {/* Category Header */}
                                {showCategoryHeader && (
                                    <div className="mb-3 mt-6 first:mt-0">
                                        <h3 className="text-lg font-bold text-[#0260CB] border-b-2 border-[#0260CB] pb-2">
                                            {line.category!.name}
                                        </h3>
                                    </div>
                                )}
                                
                                {/* SubCategory Header */}
                                {showSubCategoryHeader && (
                                    <div className="mb-2 mt-4">
                                        <h4 className="text-md font-semibold text-gray-700 pl-3 border-l-4 border-[#7090B0]">
                                            {line.subCategory!.name}
                                        </h4>
                                    </div>
                                )}
                                
                                {/* Product Card */}
                                <Card extra="p-6 !border !border-[#7090B0]/[0.25]">
                        <div className="mb-4 flex items-start justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                                    {line.productName}
                                </h3>
                                {line.productRef && (
                                    <p className="text-sm text-gray-600">Réf: {line.productRef}</p>
                                )}
                                
                                {/* Packaging or Unity */}
                                <div className="mt-1">
                                    {line.packaging && (line.packaging.ref || line.packaging.unity) ? (
                                        <p className="text-sm text-gray-600">
                                             <span className="font-medium">Conditionnement:</span> {line.packaging.unity || line.packaging.ref}
                                            {line.packaging.weight && <span className="text-gray-500"> ({line.packaging.weight} {line.unity})</span>}
                                        </p>
                                    ) : (
                                        <p className="text-sm text-gray-600">
                                            📏 <span className="font-medium">Unité:</span> {line.unity}
                                        </p>
                                    )}
                                </div>
                                
                                {/* Product Comment */}
                                {line.productComment && (
                                    <div className="mt-2 rounded-lg bg-yellow-50 p-2 text-sm">
                                        <p className="font-medium text-yellow-800">Commentaire produit :</p>
                                        <p className="mt-1 text-yellow-700">{line.productComment}</p>
                                    </div>
                                )}
                                
                                {/* Display category and subcategory */}
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {line.category && (
                                        <span className="inline-flex items-center rounded-lg bg-[#0260CB]/10 px-2 py-1 text-xs font-medium text-[#0260CB]">
                                             {line.category.name}
                                        </span>
                                    )}
                                    {line.subCategory && (
                                        <span className="inline-flex items-center rounded-lg bg-[#7090B0]/20 px-2 py-1 text-xs font-medium text-gray-700">
                                             {line.subCategory.name}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-600">Commandé</p>
                                <p className="text-lg font-bold text-navy-700 dark:text-white">
                                    {line.packaging && line.packaging.unity ? (
                                        (() => {
                                            const w = line.packaging.weight || 1;
                                            const billByBase = !!(line.packaging?.billByBaseUnit);
                                            return billByBase ? (
                                                <>
                                                    {parseFloat((line.quantityOrdered * w).toFixed(2))} {line.unity}
                                                    <span className="ml-2 text-sm text-gray-500">(= {line.quantityOrdered} {line.packaging.unity})</span>
                                                </>
                                            ) : (
                                                <>
                                                    {line.quantityOrdered} {line.packaging.unity}
                                                    <span className="ml-2 text-sm text-gray-500">({line.quantityOrdered * w} {line.unity})</span>
                                                </>
                                            );
                                        })()
                                    ) : (
                                        <>{line.quantityOrdered} {line.unity}</>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="mb-2 flex items-center justify-between">
                                <p className="font-medium text-gray-700">Quantité préparée</p>
                                <p className={`text-lg font-bold ${
                                    line.quantityPrepared >= getPreparedTarget(line) 
                                        ? 'text-green-600' 
                                        : 'text-orange-600'
                                }`}>
                                    {line.packaging && line.packaging.unity ? (
                                        (() => {
                                            const w = line.packaging.weight || 1;
                                            const billByBase = !!(line.packaging?.billByBaseUnit);
                                            return billByBase ? (
                                                <>
                                                    {parseFloat((line.quantityPrepared * w).toFixed(2))} {line.unity}
                                                    <span className="ml-2 text-sm text-gray-500">({line.quantityPrepared} {line.packaging.unity})</span>
                                                </>
                                            ) : (
                                                <>
                                                    {line.quantityPrepared} {line.packaging.unity}
                                                    <span className="ml-2 text-sm text-gray-500">({line.quantityPrepared * w} {line.unity})</span>
                                                </>
                                            );
                                        })()
                                    ) : (
                                        <>{line.quantityPrepared} {line.unity}</>
                                    )}
                                </p>
                            </div>
                            
                            {/* Checkbox to add comment on order line */}
                            {isEditable && line.quantityPrepared < getPreparedTarget(line) && (
                                <div className="mb-3 flex items-start gap-2 rounded-lg bg-blue-50 p-3">
                                    <input
                                        type="checkbox"
                                        id={`comment-${line.productId}`}
                                        checked={productsWithComment[line.productId] || false}
                                        onChange={(e) => {
                                            setProductsWithComment(prev => ({
                                                ...prev,
                                                [line.productId]: e.target.checked
                                            }));
                                        }}
                                        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-[#0260CB] focus:ring-[#0260CB]"
                                    />
                                    <label htmlFor={`comment-${line.productId}`} className="text-sm text-blue-900 cursor-pointer">
                                        Ajouter un commentaire sur la ligne de commande pour signaler le changement de quantité
                                    </label>
                                </div>
                            )}
                            
                            {line.quantityPrepared < getPreparedTarget(line) && (
                                <>
                                    <div className="rounded-lg bg-orange-50 p-2 text-sm text-orange-600">
                                        ⚠️ Quantité insuffisante ({
                                            line.packaging && line.packaging.unity ? (
                                                (() => {
                                                    const w = line.packaging.weight || 1;
                                                    const billByBase = !!(line.packaging?.billByBaseUnit);
                                                    const diff = getPreparedTarget(line) - line.quantityPrepared;
                                                    return billByBase ? (
                                                        <>
                                                            {parseFloat((diff * w).toFixed(2))} {line.unity} manquants
                                                            <span className="text-xs"> ({diff} {line.packaging.unity})</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {diff} {line.packaging.unity} manquants
                                                            <span className="text-xs"> = {diff * w} {line.unity}</span>
                                                        </>
                                                    );
                                                })()
                                            ) : (
                                                <>{getPreparedTarget(line) - line.quantityPrepared} {line.unity} manquants</>
                                            )
                                        })
                                    </div>
                                    
                                    {productInventories[line.productId] && productInventories[line.productId].length > 0 && (
                                        <div className="mt-2 rounded-lg bg-[#0260CB]/10 p-3">
                                            <p className="mb-2 text-xs font-medium text-[#0260CB]">Stocks disponibles :</p>
                                            <div className="space-y-1">
                                                {productInventories[line.productId].map((stock: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between text-xs text-[#0260CB]">
                                                        <span>
                                                            <span className="font-medium">Zone {stock.area}</span>
                                                            {stock.lotNumber && <span className="ml-2">• Lot: {stock.lotNumber}</span>}
                                                            {stock.dlc && <span className="ml-2">• DLC: {dateToFrench(stock.dlc)}</span>}
                                                        </span>
                                                        <span className="font-medium">{stock.quantity} {line.unity}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Inventory Lines Table */}
                        <div className="overflow-x-auto">
                            {editingInventory && editingInventory.productId === line.productId ? (
                                // Edition mode
                                <div className="space-y-3">
                                    <div className="rounded-lg bg-[#0260CB]/10 p-3 text-sm text-[#0260CB]">
                                        💡 <strong>Plusieurs lots disponibles ?</strong> Utilisez le bouton "+ Ajouter ligne" pour picker le même produit avec différents lots/zones de stock.
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-medium text-navy-700">Édition des lignes d'inventaire</h4>
                                            <p className="mt-1 text-sm text-gray-600">
                                                Total à prélever: {' '}
                                                <span className={`font-bold ${
                                                    editingInventory.inventoryLines.reduce((sum, inv) => sum + (inv.quantityPicked || 0), 0) >= line.quantityOrdered
                                                        ? 'text-green-600' 
                                                        : 'text-orange-600'
                                                }`}>
                                                    {editingInventory.inventoryLines.reduce((sum, inv) => sum + (inv.quantityPicked || 0), 0)} / {line.quantityOrdered} {line.unity}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {saveStatus === 'saving' && (
                                                <span className="text-sm text-gray-600">
                                                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-orange-500 mr-2"></span>
                                                    Sauvegarde...
                                                </span>
                                            )}
                                            {saveStatus === 'saved' && (
                                                <span className="text-sm text-green-600">
                                                    <MdCheck className="inline h-4 w-4 mr-1" />
                                                    Sauvegardé
                                                </span>
                                            )}
                                            {saveStatus === 'error' && (
                                                <span className="text-sm text-red-600">
                                                    <MdClose className="inline h-4 w-4 mr-1" />
                                                    Erreur
                                                </span>
                                            )}
                                            <button
                                                onClick={handleAddInventoryLine}
                                                className="linear rounded-lg bg-green-500 px-3 py-1 text-sm text-white transition duration-200 hover:bg-green-600 active:bg-green-700"
                                            >
                                                + Ajouter ligne
                                            </button>
                                            <button
                                                onClick={handleCancelEditInventory}
                                                className="linear rounded-lg bg-gray-500 px-3 py-1 text-sm text-white transition duration-200 hover:bg-gray-600 active:bg-gray-700"
                                            >
                                                Fermer
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {editingInventory.inventoryLines.map((inv, invIndex) => (
                                        <div key={invIndex} className="rounded-lg border border-gray-200 p-3">
                                            <div className="mb-2 flex items-center justify-between">
                                                <span className="text-sm font-medium text-gray-600">Ligne {invIndex + 1}</span>
                                                <button
                                                    onClick={() => handleRemoveInventoryLine(invIndex)}
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    ✕ Supprimer
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                                <div className="md:col-span-2">
                                                    <label className="mb-1 block text-xs text-gray-600">Sélectionner le stock *</label>
                                                    <select
                                                        value={inv.inventoryId || ''}
                                                        onChange={(e) => {
                                                            if (!editingInventory) return;
                                                            
                                                            const selectedValue = e.target.value;
                                                            
                                                            if (selectedValue === 'MANUAL') {
                                                                // Manual
                                                                const updatedLines = [...editingInventory.inventoryLines];
                                                                updatedLines[invIndex] = {
                                                                    ...updatedLines[invIndex],
                                                                    inventoryId: 'MANUAL',
                                                                    area: '',
                                                                    lotNumber: '',
                                                                    dlc: '',
                                                                    quantityAvailable: 9999,
                                                                };
                                                                
                                                                setEditingInventory({
                                                                    ...editingInventory,
                                                                    inventoryLines: updatedLines
                                                                });
                                                            } else {
                                                                const productInventory = productInventories[editingInventory.productId] || [];
                                                                const selectedInventory = productInventory.find(i => i.id === selectedValue);
                                                                if (selectedInventory) {
                                                                    const updatedLines = [...editingInventory.inventoryLines];
                                                                    updatedLines[invIndex] = {
                                                                        ...updatedLines[invIndex],
                                                                        inventoryId: selectedInventory.id,
                                                                        area: selectedInventory.area,
                                                                        lotNumber: selectedInventory.lotNumber || '',
                                                                        dlc: selectedInventory.dlc || '',
                                                                        quantityAvailable: selectedInventory.quantity,
                                                                    };
                                                                    
                                                                    setEditingInventory({
                                                                        ...editingInventory,
                                                                        inventoryLines: updatedLines
                                                                    });
                                                                }
                                                            }
                                                        }}
                                                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                                                        <option value="">-- Choisir un stock --</option>
                                                        <option value="MANUAL" className="font-semibold text-[#0260CB]">Saisie manuelle (sans stock)</option>
                                                        {(productInventories[editingInventory.productId] || [])
                                                            .filter(stock => {
                                                                // Show stock if not already selected in another line (except current line)
                                                                const alreadySelected = editingInventory.inventoryLines.some(
                                                                    (line, idx) => idx !== invIndex && line.inventoryId === stock.id
                                                                );
                                                                return !alreadySelected;
                                                            })
                                                            .map((stock) => (
                                                            <option key={stock.id} value={stock.id}>
                                                                {stock.area} | Lot: {stock.lotNumber || 'N/A'} | DLC: {stock.dlc ? dateToFrench(stock.dlc) : 'N/A'} | Dispo: {stock.quantity}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Zone {inv.inventoryId === 'MANUAL' ? '*' : '(lecture seule)'}</label>
                                                    <input
                                                        type="text"
                                                        value={inv.area}
                                                        disabled={inv.inventoryId !== 'MANUAL'}
                                                        onChange={(e) => {
                                                            if (inv.inventoryId === 'MANUAL') {
                                                                handleUpdateInventoryLine(invIndex, 'area', e.target.value);
                                                            }
                                                        }}
                                                        className={`w-full rounded-lg border border-gray-300 px-2 py-1 text-sm ${inv.inventoryId !== 'MANUAL' ? 'bg-gray-100' : 'focus:border-[#0260CB] focus:outline-none'}`}
                                                        placeholder={inv.inventoryId === 'MANUAL' ? 'Ex: A1, B2...' : ''}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Lot {inv.inventoryId === 'MANUAL' ? '' : '(lecture seule)'}</label>
                                                    <input
                                                        type="text"
                                                        value={inv.lotNumber || ''}
                                                        disabled={inv.inventoryId !== 'MANUAL'}
                                                        onChange={(e) => {
                                                            if (inv.inventoryId === 'MANUAL') {
                                                                handleUpdateInventoryLine(invIndex, 'lotNumber', e.target.value);
                                                            }
                                                        }}
                                                        className={`w-full rounded-lg border border-gray-300 px-2 py-1 text-sm ${inv.inventoryId !== 'MANUAL' ? 'bg-gray-100' : 'focus:border-[#0260CB] focus:outline-none'}`}
                                                        placeholder={inv.inventoryId === 'MANUAL' ? 'Numéro de lot' : ''}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">DLC {inv.inventoryId === 'MANUAL' ? '' : '(lecture seule)'}</label>
                                                    <input
                                                        type="text"
                                                        value={inv.dlc ? (inv.inventoryId === 'MANUAL' ? inv.dlc : dateToFrench(inv.dlc)) : ''}
                                                        disabled={inv.inventoryId !== 'MANUAL'}
                                                        onChange={(e) => {
                                                            if (inv.inventoryId === 'MANUAL') {
                                                                handleUpdateInventoryLine(invIndex, 'dlc', e.target.value);
                                                            }
                                                        }}
                                                        className={`w-full rounded-lg border border-gray-300 px-2 py-1 text-sm ${inv.inventoryId !== 'MANUAL' ? 'bg-gray-100' : 'focus:border-[#0260CB] focus:outline-none'}`}
                                                        placeholder={inv.inventoryId === 'MANUAL' ? 'JJ/MM/AAAA' : ''}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Dispo (lecture seule)</label>
                                                    <input
                                                        type="number"
                                                        value={inv.inventoryId === 'MANUAL' ? '-' : inv.quantityAvailable}
                                                        disabled
                                                        className="w-full rounded-lg border border-gray-300 bg-gray-100 px-2 py-1 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs text-gray-600">Qté à prélever *</label>
                                                    <input
                                                        type="number"
                                                        value={inv.quantityPicked}
                                                        onChange={(e) => handleUpdateInventoryLine(invIndex, 'quantityPicked', parseFloat(e.target.value) || 0)}
                                                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-[#0260CB] focus:outline-none"
                                                        min="0"
                                                        max={inv.inventoryId === 'MANUAL' ? undefined : inv.quantityAvailable}
                                                        step="0.01"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Mode lecture
                                <>
                                    {isEditable && (
                                        <div className="mb-3 flex justify-end">
                                            <button
                                                onClick={() => handleEditInventoryLines(line.productId, line.inventoryLines)}
                                                className="linear flex items-center gap-1 rounded-lg bg-[#0260CB] px-3 py-1 text-sm text-white transition duration-200 hover:bg-[#01499b] active:bg-[#024086]"
                                            >
                                                <MdEdit size={16} />
                                                Modifier
                                            </button>
                                        </div>
                                    )}
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="pb-2 text-left text-sm font-medium text-gray-600">Zone</th>
                                                <th className="pb-2 text-left text-sm font-medium text-gray-600">Lot</th>
                                                <th className="pb-2 text-left text-sm font-medium text-gray-600">DLC</th>
                                                <th className="pb-2 text-right text-sm font-medium text-gray-600">Quantité prélevée</th>
                                                <th className="pb-2 text-right text-sm font-medium text-gray-600">Dispo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {line.inventoryLines.map((inv: IPreparationInventoryLine, invIndex: number) => (
                                                <tr key={invIndex} className="border-b border-gray-100">
                                                    <td className="py-3 text-sm text-navy-700 dark:text-white">
                                                        {inv.area}
                                                    </td>
                                                    <td className="py-3 text-sm text-navy-700 dark:text-white">
                                                        {inv.lotNumber || 'N/A'}
                                                    </td>
                                                    <td className="py-3 text-sm text-navy-700 dark:text-white">
                                                        {inv.dlc ? dateToFrench(inv.dlc) : 'N/A'}
                                                    </td>
                                                    <td className="py-3 text-right text-sm font-semibold text-navy-700 dark:text-white">
                                                        {inv.quantityPicked}
                                                    </td>
                                                    <td className="py-3 text-right text-sm text-gray-600">
                                                        {inv.quantityAvailable}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>

                        {line.notes && (
                            <div className="mt-4 rounded-lg bg-blue-50 p-3">
                                <p className="text-sm font-medium text-blue-900">Notes:</p>
                                <p className="text-sm text-blue-700">{line.notes}</p>
                            </div>
                        )}
                    </Card>
                            </div>
                        );
                    });
                })()}
            </div>

            {/* BL Modal */}
            {showBLModal && (() => {
                const orderId = typeof preparation.orderId === 'object'
                    ? (preparation.orderId as any)._id || preparation.orderId
                    : preparation.orderId;

                const handleAction = async (withPrice: boolean, action: 'download' | 'print') => {
                    const blob = withPrice
                        ? await getOrderDeliveryNote(orderId as string)
                        : await getOrderDeliveryNoteWithoutPrice(orderId as string);
                    if (!blob) return;
                    const url = window.URL.createObjectURL(blob);
                    if (action === 'download') {
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `BL-${preparation.orderNumber}.pdf`;
                        link.click();
                    } else {
                        const win = window.open(url, '_blank');
                        if (win) { win.focus(); setTimeout(() => win.print(), 800); }
                    }
                    setShowBLModal(false);
                };

                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                        <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800">
                            <button
                                onClick={() => setShowBLModal(false)}
                                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                            >
                                <MdClose className="h-5 w-5" />
                            </button>

                            <h3 className="mb-1 text-lg font-bold text-navy-700 dark:text-white">Bon de livraison</h3>
                            <p className="mb-5 text-sm text-gray-500">Choisissez le type de BL et l'action souhaitée.</p>

                            {/* With price */}
                            <div className="mb-3 rounded-xl border border-[#7090B0]/30 p-4">
                                <p className="mb-3 text-sm font-semibold text-navy-700 dark:text-white">BL valorisé (avec prix)</p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleAction(true, 'download')}
                                        className="linear flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#0260CB] px-3 py-2 text-xs font-medium text-white transition duration-200 hover:bg-[#01499b]"
                                    >
                                        <MdDownload className="h-4 w-4" />
                                        Télécharger
                                    </button>
                                    <button
                                        onClick={() => handleAction(true, 'print')}
                                        className="linear flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#0260CB] px-3 py-2 text-xs font-medium text-[#0260CB] transition duration-200 hover:bg-[#0260CB]/10"
                                    >
                                        <MdPrint className="h-4 w-4" />
                                        Imprimer
                                    </button>
                                </div>
                            </div>

                            {/* Without price */}
                            <div className="rounded-xl border border-[#7090B0]/30 p-4">
                                <p className="mb-3 text-sm font-semibold text-navy-700 dark:text-white">BL non valorisé (sans prix)</p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleAction(false, 'download')}
                                        className="linear flex flex-1 items-center justify-center gap-2 rounded-xl bg-gray-600 px-3 py-2 text-xs font-medium text-white transition duration-200 hover:bg-gray-700"
                                    >
                                        <MdDownload className="h-4 w-4" />
                                        Télécharger
                                    </button>
                                    <button
                                        onClick={() => handleAction(false, 'print')}
                                        className="linear flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-600 px-3 py-2 text-xs font-medium text-gray-600 transition duration-200 hover:bg-gray-600/10"
                                    >
                                        <MdPrint className="h-4 w-4" />
                                        Imprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Validation Modal */}
            <ConfirmModal
                isOpen={showValidateModal}
                onClose={() => setShowValidateModal(false)}
                onConfirm={confirmValidate}
                title={validateModalContent.title}
                description={validateModalContent.description}
                confirmText={validateModalContent.isWarning && validateModalExtraAction ? 'Forcer avec 0' : 'Valider'}
                cancelText="Annuler"
                confirmColor={validateModalContent.isWarning ? "bg-orange-500" : "bg-green-500"}
                isWarning={validateModalContent.isWarning}
                extraAction={validateModalExtraAction}
            />

            {/* Cancel Modal */}
            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={confirmCancel}
                title="Annuler la préparation"
                description="Êtes-vous sûr de vouloir annuler cette préparation ? Les données seront conservées mais le statut sera changé à 'Annulée'."
                confirmText="Annuler la préparation"
                cancelText="Retour"
                confirmColor="bg-red-500"
                isWarning={true}
            />
        </div>
    );
};

export default ViewPreparation;
